import { useState } from "react";
import { toast } from "sonner";
import { Loader2, Upload } from "lucide-react";
import api, { fileUrl } from "@/lib/api";
import { InlineSignaturePad } from "@/components/InlineSignaturePad";

export default function VCheckSignatureBlock({ field, doc, projectId, onChanged }) {
  const [saving, setSaving] = useState(false);
  const [mode, setMode] = useState("canvas");
  const [currentPng, setCurrentPng] = useState(null);
  const sig = doc?.customer_signature;

  const save = async () => {
    if (!currentPng) return toast.error("Bitte zuerst unterschreiben");
    setSaving(true);
    try {
      await api.post(`/projects/${projectId}/vcheck-wp/sign`, {
        signature_png_dataurl: currentPng, method: "canvas",
        signed_name: doc?.data?.signed_name,
        signed_place: doc?.data?.signed_place,
        signed_date: doc?.data?.signed_date,
      });
      toast.success("Unterschrift gespeichert");
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Speichern fehlgeschlagen");
    } finally { setSaving(false); }
  };

  const uploadPhoto = async (file) => {
    if (!file) return;
    setSaving(true);
    try {
      const form = new FormData();
      form.append("file", file);
      await api.post(`/projects/${projectId}/vcheck-wp/photos/customer_signature/upload`, form,
        { headers: { "Content-Type": "multipart/form-data" } });
      await api.post(`/projects/${projectId}/vcheck-wp/sign`, {
        method: "upload",
        signed_name: doc?.data?.signed_name,
        signed_place: doc?.data?.signed_place,
        signed_date: doc?.data?.signed_date,
      });
      toast.success("Foto-Unterschrift hochgeladen");
      onChanged?.();
    } catch (e) { toast.error("Foto-Upload fehlgeschlagen"); }
    finally { setSaving(false); }
  };

  const reset = async () => {
    if (!window.confirm("Aktuelle Unterschrift entfernen?")) return;
    try {
      await api.post(`/projects/${projectId}/vcheck-wp/sign`, {
        method: "canvas", signature_png_dataurl: null,
      }).catch(() => {});
      toast.info("Bitte im Backend erneut signieren");
      onChanged?.();
    } catch (e) { /* silent */ }
  };

  return (
    <div className="rounded-lg border border-zinc-800 bg-zinc-900 p-3 space-y-3" data-testid="vcheck-signature-block">
      <div className="flex items-center justify-between">
        <div className="text-[12.5px] font-semibold text-zinc-100">{field.label}
          <span className="text-[10px] text-zinc-500 ml-2">(optional bei Erst-Freigabe · nachdokumentierbar)</span>
        </div>
        {sig && (
          <div className="flex items-center gap-2">
            <span className="text-[11px] px-2 py-0.5 rounded-md bg-emerald-500/15 border border-emerald-500/40 text-emerald-300">
              ✓ signiert · {sig.method}
            </span>
            <button type="button" onClick={reset} className="text-[10.5px] text-zinc-500 hover:text-zinc-300 underline">Neu signieren</button>
          </div>
        )}
      </div>
      {field.hint && <div className="text-[10.5px] text-zinc-500">{field.hint}</div>}

      {sig?.png_path && (
        <img src={fileUrl(sig.png_path)} alt="Unterschrift" className="max-w-xs bg-white rounded border border-zinc-700" />
      )}

      {!sig && (
        <>
          <div className="flex gap-2">
            <button type="button" onClick={() => setMode("canvas")}
                    className={`px-3 h-8 rounded-md text-[11.5px] ${mode === "canvas" ? "bg-emerald-600 text-zinc-950" : "bg-zinc-800 text-zinc-300"}`}
                    data-testid="vcheck-sig-mode-canvas">Digital signieren</button>
            <button type="button" onClick={() => setMode("upload")}
                    className={`px-3 h-8 rounded-md text-[11.5px] ${mode === "upload" ? "bg-emerald-600 text-zinc-950" : "bg-zinc-800 text-zinc-300"}`}
                    data-testid="vcheck-sig-mode-upload">Foto der Papier-Unterschrift</button>
          </div>
          {mode === "canvas" ? (
            <>
              <InlineSignaturePad onChange={setCurrentPng} height={140} testId="vcheck-sig" />
              <button type="button" onClick={save} disabled={saving || !currentPng}
                      className="h-9 px-4 rounded-md bg-emerald-500 hover:bg-emerald-400 text-zinc-950 font-bold text-[12.5px] disabled:opacity-50"
                      data-testid="vcheck-sig-save">
                {saving ? <Loader2 size={12} className="inline animate-spin mr-1" /> : null}
                Unterschrift speichern
              </button>
            </>
          ) : (
            <label className="inline-flex items-center gap-2 h-9 px-3 rounded-md bg-emerald-500 hover:bg-emerald-400 text-zinc-950 font-bold text-[12.5px] cursor-pointer">
              <Upload size={12} /> Foto wählen
              <input type="file" accept="image/*" className="hidden"
                     onChange={(e) => uploadPhoto(e.target.files?.[0])}
                     data-testid="vcheck-sig-upload-input" />
            </label>
          )}
        </>
      )}
    </div>
  );
}
